/**
 * 房间资源板 — 以房间为主记录，横向比较设备构成、系统类型与排产配置。
 */
import React, { useMemo, useState } from 'react';
import {
  WxbActionMenu,
  WxbButton,
  WxbEmpty,
  WxbIconButton,
  WxbStatusBadge,
  WxbTag,
} from '../wxb-ui';
import type { ResourceNode, ResourceNodeClass } from '../ProcessTemplateV2/types';
import {
  BINDABLE_CLASSES,
  NODE_CLASS_LABEL,
  allowedChildBlueprints,
} from './resourceNodeConstants';

interface EquipmentCardViewProps {
  nodes: ResourceNode[];
  selectedNodeId: number | null;
  canWrite: boolean;
  onSelect: (nodeId: number) => void;
  onEdit: (node: ResourceNode) => void;
  onDelete: (node: ResourceNode) => void;
  onToggleActive: (node: ResourceNode) => void;
  onCreateChild: (parent: ResourceNode, preferredNodeClass?: ResourceNodeClass) => void;
}

interface RoomBoardItem {
  node: ResourceNode;
  path: string;
}

interface RoomBoardGroup {
  key: string;
  label: string;
  code: string | null;
  nodeClass: ResourceNodeClass | null;
  rooms: RoomBoardItem[];
}

const flattenChildren = (node: ResourceNode): ResourceNode[] => {
  const result: ResourceNode[] = [];
  const walk = (items: ResourceNode[]) => {
    items.forEach((item) => {
      result.push(item);
      if (item.children.length) walk(item.children);
    });
  };
  walk(node.children);
  return result;
};

const buildRoomGroups = (nodes: ResourceNode[]): RoomBoardGroup[] => {
  const groups = new Map<string, RoomBoardGroup>();

  const walk = (items: ResourceNode[], ancestors: ResourceNode[]) => {
    items.forEach((node) => {
      if (node.nodeClass === 'ROOM') {
        const owner = [...ancestors]
          .reverse()
          .find((ancestor) => ancestor.nodeClass === 'LINE')
          ?? [...ancestors].reverse().find((ancestor) => ancestor.nodeClass === 'SITE')
          ?? null;
        const key = owner ? `${owner.nodeClass}-${owner.id}` : 'ungrouped';
        const group = groups.get(key) ?? {
          key,
          label: owner?.nodeName ?? '未分组房间',
          code: owner?.nodeCode ?? null,
          nodeClass: owner?.nodeClass ?? null,
          rooms: [],
        };
        group.rooms.push({
          node,
          path: [...ancestors, node].map((item) => item.nodeName).join(' / '),
        });
        groups.set(key, group);
      }

      if (node.children.length) {
        walk(node.children, [...ancestors, node]);
      }
    });
  };

  walk(nodes, []);
  return Array.from(groups.values());
};

const resourceToneClass = (node: ResourceNode): string => {
  if (node.nodeClass === 'UTILITY_STATION') return 'is-utility';
  if (node.equipmentSystemType === 'SUS') return 'is-sus';
  if (node.equipmentSystemType === 'SS') return 'is-ss';
  if (node.equipmentSystemType === 'VIRTUAL') return 'is-virtual';
  return 'is-neutral';
};

const systemTagColor = (systemType: ResourceNode['equipmentSystemType']) => {
  if (systemType === 'SUS') return 'green' as const;
  if (systemType === 'SS') return 'blue' as const;
  if (systemType === 'VIRTUAL') return 'amber' as const;
  return 'neutral' as const;
};

const schedulingLabel = (node: ResourceNode): string => {
  if (!node.boundResourceId) return '待配置排产';
  if (node.boundResourceStatus === 'MAINTENANCE') return '维护中';
  if (node.boundResourceStatus === 'RETIRED') return '已退役';
  if (node.boundResourceStatus !== 'ACTIVE' || !node.boundResourceIsSchedulable) return '暂停排产';
  return node.boundResourceCode ?? '已配置排产';
};

const schedulingReady = (node: ResourceNode): boolean => Boolean(
  node.boundResourceId
  && node.boundResourceStatus === 'ACTIVE'
  && node.boundResourceIsSchedulable,
);

const RoomBoardRow: React.FC<{
  item: RoomBoardItem;
  selectedNodeId: number | null;
  canWrite: boolean;
  onSelect: (nodeId: number) => void;
  onEdit: (node: ResourceNode) => void;
  onDelete: (node: ResourceNode) => void;
  onToggleActive: (node: ResourceNode) => void;
  onCreateChild: (parent: ResourceNode, preferredNodeClass?: ResourceNodeClass) => void;
}> = ({
  item,
  selectedNodeId,
  canWrite,
  onSelect,
  onEdit,
  onDelete,
  onToggleActive,
  onCreateChild,
}) => {
  const room = item.node;
  const descendants = useMemo(() => flattenChildren(room), [room]);
  const resources = useMemo(
    () => descendants.filter((node) => BINDABLE_CLASSES.has(node.nodeClass)),
    [descendants],
  );
  const equipment = useMemo(
    () => descendants.filter((node) => node.nodeClass === 'EQUIPMENT_UNIT'),
    [descendants],
  );
  const [resourcesExpanded, setResourcesExpanded] = useState(false);
  const readyCount = resources.filter(schedulingReady).length;
  const issueNodeIds = new Set<number>();
  [room, ...descendants].forEach((node) => {
    if (!node.isActive) issueNodeIds.add(node.id);
  });
  resources.forEach((node) => {
    if (!schedulingReady(node)) issueNodeIds.add(node.id);
  });
  const issueCount = issueNodeIds.size;
  const hiddenResourceCount = Math.max(0, resources.length - 6);
  const visibleResources = resourcesExpanded ? resources : resources.slice(0, 6);
  const ssCount = equipment.filter((node) => node.equipmentSystemType === 'SS').length;
  const susCount = equipment.filter((node) => node.equipmentSystemType === 'SUS').length;
  const virtualCount = equipment.filter((node) => node.equipmentSystemType === 'VIRTUAL').length;
  const canCreateChild = allowedChildBlueprints(room).length > 0;
  const isSelected = room.id === selectedNodeId;

  const menuActions = [
    { key: 'edit', label: '编辑房间', onClick: () => onEdit(room) },
    ...(canCreateChild
      ? [{
          key: 'create',
          label: room.nodeSubtype === 'MAIN_PROCESS' ? '新增设备' : '新增子节点',
          onClick: () => onCreateChild(
            room,
            room.nodeSubtype === 'MAIN_PROCESS' ? 'EQUIPMENT_UNIT' : undefined,
          ),
        }]
      : []),
    {
      key: 'toggle',
      label: room.isActive ? '停用' : '启用',
      onClick: () => onToggleActive(room),
    },
    {
      key: 'delete',
      label: '删除',
      danger: true,
      disabled: room.childCount > 0,
      onClick: () => onDelete(room),
      confirm: {
        title: '确定删除此房间？',
        description: room.childCount > 0 ? '该房间有子节点，请先删除子节点。' : undefined,
      },
    },
  ];

  return (
    <div className={`equip-room-row ${isSelected ? 'is-selected' : ''}`}>
      <div className="equip-room-identity">
        <div className="equip-room-title-line">
          <WxbTag color="green">房间</WxbTag>
          <WxbButton
            variant="ghost"
            size="sm"
            className="equip-room-name-button"
            onClick={() => onSelect(room.id)}
          >
            {room.nodeName}
          </WxbButton>
        </div>
        <div className="equip-room-code" title={room.nodeCode}>{room.nodeCode}</div>
        <div className="equip-room-path" title={item.path}>{item.path}</div>
      </div>

      <div className="equip-room-resources">
        {visibleResources.length ? (
          <div className="equip-room-resource-grid">
            {visibleResources.map((resource) => (
              <WxbButton
                key={resource.id}
                variant="ghost"
                size="sm"
                className="equip-room-resource-button"
                onClick={() => onSelect(resource.id)}
              >
                <span className={`equip-room-resource-mark ${resourceToneClass(resource)}`} />
                <span className="equip-room-resource-copy">
                  <span className="equip-room-resource-name" title={resource.nodeName}>
                    {resource.nodeName}
                  </span>
                  <span
                    className={`equip-room-resource-code ${resource.boundResourceId ? '' : 'is-unbound'}`}
                    title={schedulingLabel(resource)}
                  >
                    {schedulingLabel(resource)}
                  </span>
                </span>
              </WxbButton>
            ))}
            {hiddenResourceCount > 0 && (
              <WxbButton
                variant="ghost"
                size="sm"
                className="equip-room-resource-more"
                aria-expanded={resourcesExpanded}
                onClick={() => setResourcesExpanded((expanded) => !expanded)}
              >
                {resourcesExpanded
                  ? `收起 ${hiddenResourceCount} 个节点`
                  : `展开其余 ${hiddenResourceCount} 个节点`}
              </WxbButton>
            )}
          </div>
        ) : (
          <span className="equip-room-muted">暂无排产设备或工作站</span>
        )}
      </div>

      <div className="equip-room-systems">
        {ssCount > 0 && <WxbTag color={systemTagColor('SS')}>SS {ssCount}</WxbTag>}
        {susCount > 0 && <WxbTag color={systemTagColor('SUS')}>SUS {susCount}</WxbTag>}
        {virtualCount > 0 && <WxbTag color={systemTagColor('VIRTUAL')}>虚拟 {virtualCount}</WxbTag>}
        {!equipment.length && <span className="equip-room-muted">—</span>}
      </div>

      <div className="equip-room-binding">
        <span className={readyCount < resources.length ? 'is-incomplete' : ''}>
          {readyCount} / {resources.length}
        </span>
        <span className="equip-room-binding-label">已配置排产</span>
      </div>

      <div className="equip-room-health">
        <WxbStatusBadge
          status={!room.isActive ? 'disabled' : issueCount > 0 ? 'warning' : 'success'}
          label={!room.isActive ? '已停用' : issueCount > 0 ? `${issueCount} 项待处理` : '完整'}
          variant="dot"
          size="sm"
        />
      </div>

      <div className="equip-room-actions">
        {canWrite ? (
          <WxbActionMenu
            actions={menuActions}
            triggerAriaLabel={`${room.nodeName} 更多操作`}
            iconOnly
            size="sm"
          />
        ) : (
          <WxbIconButton
            icon="inspect"
            label={`查看 ${room.nodeName}`}
            size="sm"
            variant="ghost"
            onClick={() => onSelect(room.id)}
          />
        )}
      </div>
    </div>
  );
};

const EquipmentCardView: React.FC<EquipmentCardViewProps> = ({
  nodes,
  selectedNodeId,
  canWrite,
  onSelect,
  onEdit,
  onDelete,
  onToggleActive,
  onCreateChild,
}) => {
  const groups = useMemo(() => buildRoomGroups(nodes), [nodes]);

  if (!groups.length) {
    return <WxbEmpty description="没有符合当前筛选条件的房间资源" />;
  }

  return (
    <div className="equip-room-board">
      {groups.map((group) => {
        const resourceCount = group.rooms.reduce(
          (total, room) => total + flattenChildren(room.node)
            .filter((node) => BINDABLE_CLASSES.has(node.nodeClass)).length,
          0,
        );
        return (
          <section key={group.key} className="equip-room-group">
            <div className="equip-room-group-header">
              <div className="equip-room-group-title">
                {group.nodeClass && <WxbTag color={group.nodeClass === 'LINE' ? 'cyan' : 'blue'}>
                  {NODE_CLASS_LABEL[group.nodeClass]}
                </WxbTag>}
                <span>{group.label}</span>
                {group.code && <span className="equip-room-group-code">{group.code}</span>}
              </div>
              <span className="equip-room-group-summary">
                {group.rooms.length} 个房间 · {resourceCount} 个排产节点
              </span>
            </div>

            <div className="equip-room-column-header" aria-hidden="true">
              <span>房间</span>
              <span>设备与排产状态</span>
              <span>系统构成</span>
              <span>排产配置</span>
              <span>状态</span>
              <span />
            </div>

            <div className="equip-room-group-body">
              {group.rooms.map((item) => (
                <RoomBoardRow
                  key={item.node.id}
                  item={item}
                  selectedNodeId={selectedNodeId}
                  canWrite={canWrite}
                  onSelect={onSelect}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onToggleActive={onToggleActive}
                  onCreateChild={onCreateChild}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default EquipmentCardView;
